/* eslint-disable react/no-array-index-key */
import React from 'react';
import { SortableElement } from 'react-sortable-hoc';
import {
  type CustomButton,
  CustomButtonType,
  FeatureType,
  ImageType,
  PanelType,
} from '@types';
import Image from '@components/Image';
import IconTrash from '@components/icons/IconTrash';
import Icon from '@components/Icon';
import TextLink from '@components/TextLink';
import useGetFeatureTitle from '@hooks/useGetFeatureTitle';
import SortableList from '@components/SortableList';
import arrayMove from '@utils/arrayMove';
import { $panelApi } from '@stores/panelStores';
import FeatureIcon from '@components/FeatureIcon';
import { customButtonDefault } from '@modules/edit';
import CustomButtonSubItem from './CustomButtonSubItem';
import ImgLast from './img-last.png';
import { useCustomButtonContext } from './context';

interface Props {
  data: CustomButton;
  groupIndex: number;
  subMax: number;
  sorting: boolean;
}

function CustomButtonItem({ data, groupIndex, subMax, sorting }: Props) {
  const { onChange } = useCustomButtonContext();
  const getFTitle = useGetFeatureTitle();
  const isGroup = data.type === CustomButtonType.group;
  const title = isGroup ? data.groupTitle : data.children[0].title;
  const defaultTitle = getFTitle(isGroup ? '_group' : data.children[0].code);

  const createNewSubItem = (gIndex: number, buttonIndex: number) => {
    onChange((draft) => {
      draft[gIndex].children[buttonIndex] = customButtonDefault(
        FeatureType.link
      );
    });
    $panelApi.show({
      type: PanelType.customButton,
      groupIndex: gIndex,
      buttonIndex,
      isNew: true,
    });
  };

  return (
    <div className="mt-10 select-none rounded bg-black-border text-base text-white">
      <div className="flex h-35 items-center px-10">
        {data.iconMediaUrl ? (
          <Image
            src={data.iconMediaUrl}
            className="mr-5 h-16 w-16"
            type={data.iconMediaId === 0 ? ImageType.sysMedia : ImageType.media}
          />
        ) : (
          <FeatureIcon
            type={isGroup ? '_group' : data.children[0].code}
            className="mr-5 h-16 w-16 flex-shrink-0"
          />
        )}
        <div
          className="mr-10 flex-1 cursor-pointer truncate"
          onClick={() => {
            if (isGroup) {
              $panelApi.show({
                type: PanelType.customGroup,
                groupIndex,
              });
            } else {
              $panelApi.show({
                type: PanelType.customButton,
                groupIndex,
                buttonIndex: 0,
              });
            }
          }}
        >
          {title || defaultTitle}
        </div>
        <IconTrash
          className="flex-shrink-0"
          onClick={() => {
            onChange((draft) => {
              draft.splice(groupIndex, 1);
            });
          }}
        />
      </div>
      {isGroup && !sorting && (
        <div className="pl-5 pb-10">
          <SortableList
            axis="y"
            lockAxis="y"
            distance={10}
            onSortEnd={(sort) => {
              onChange((draft) => {
                draft[groupIndex].children = arrayMove({
                  array: draft[groupIndex].children,
                  fromIndex: sort.oldIndex,
                  toIndex: sort.newIndex,
                });
              });
            }}
          >
            <div>
              {data.children.map((x, i) => (
                <CustomButtonSubItem
                  key={i}
                  groupIndex={groupIndex}
                  title={x.title || getFTitle(x.code)}
                  index={i}
                  itemIndex={i}
                  subMax={subMax}
                />
              ))}
            </div>
          </SortableList>
          {data.children.length < subMax && (
            <div className="flex h-30 items-center px-10">
              <Icon className="mr-10 flex-shrink-0" src={ImgLast} />
              <TextLink
                onClick={() =>
                  createNewSubItem(groupIndex, data.children.length)
                }
              >
                + 创建子按钮
              </TextLink>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default SortableElement(CustomButtonItem);
